import { MAIN_TABS, TabId } from '../navigation/tabs'
import { isRestaurantOnly } from '../config/restaurantOnly'
import type { ReactNode } from 'react'

type BottomTabBarProps = {
  activeTab: TabId
  onTabChange: (tabId: TabId) => void
  chatUnreadCount?: number
  cartCount?: number
  analyticsLocked?: boolean
}

const IconSearch = () => (
  <svg className="rp-tab__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
)

const IconCart = () => (
  <svg className="rp-tab__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6h15l-1.5 9h-12z" />
    <path d="M6 6 5 3H2" />
    <circle cx="9" cy="20" r="1.4" />
    <circle cx="18" cy="20" r="1.4" />
  </svg>
)

const IconPrices = () => (
  <svg className="rp-tab__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 5h16v14H4z" />
    <path d="M8 9h8M8 13h8M8 17h5" />
  </svg>
)

const IconAnalytics = () => (
  <svg className="rp-tab__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19V5M4 19h16" />
    <path d="m7 14 3-4 3 3 4-6" />
  </svg>
)

const IconOrders = () => (
  <svg className="rp-tab__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 6h13M8 12h13M8 18h13" />
    <path d="M3 6h.01M3 12h.01M3 18h.01" />
  </svg>
)

const IconChat = () => (
  <svg className="rp-tab__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
  </svg>
)

const tabIcon = (tabId: TabId): ReactNode => {
  switch (tabId) {
    case TabId.Search:
      return <IconSearch />
    case TabId.Cart:
      return <IconCart />
    case TabId.Prices:
      return <IconPrices />
    case TabId.Analytics:
      return <IconAnalytics />
    case TabId.Orders:
      return <IconOrders />
    case TabId.Chat:
      return <IconChat />
    default:
      return null
  }
}

export const BottomTabBar = ({
  activeTab,
  onTabChange,
  chatUnreadCount = 0,
  cartCount = 0,
  analyticsLocked = false,
}: BottomTabBarProps) => (
  <nav
    className={isRestaurantOnly ? 'rp-tabbar' : 'echat-tab-bar'}
    aria-label="Основная навигация"
  >
    {MAIN_TABS.map((tab) => {
      const isActive = tab.id === activeTab
      const showChatUnread = !isRestaurantOnly && tab.id === TabId.Chat && chatUnreadCount > 0
      const showCartCount = isRestaurantOnly && tab.id === TabId.Cart && cartCount > 0
      const badgeValue = showChatUnread
        ? chatUnreadCount
        : showCartCount
          ? cartCount
          : 0
      const showPro = isRestaurantOnly && tab.id === TabId.Analytics && analyticsLocked

      return (
        <button
          key={tab.id}
          type="button"
          className={
            isRestaurantOnly
              ? `rp-tab${isActive ? ' rp-tab--on' : ''}`
              : `echat-tab-bar__item${isActive ? ' echat-tab-bar__item--active' : ''}`
          }
          aria-current={isActive ? 'page' : undefined}
          onClick={() => onTabChange(tab.id)}
        >
          {isRestaurantOnly ? tabIcon(tab.id) : null}
          <span className={isRestaurantOnly ? 'rp-tab__label' : undefined}>{tab.label}</span>
          {badgeValue > 0 ? (
            <span
              className={isRestaurantOnly ? 'rp-tab__badge' : 'echat-tab-bar__badge'}
              aria-label={
                showCartCount
                  ? `В корзине: ${badgeValue}`
                  : `Непрочитанных сообщений: ${badgeValue}`
              }
            >
              {badgeValue > 99 ? '99+' : badgeValue}
            </span>
          ) : null}
          {showPro ? (
            <span className="rp-tab__pro" aria-label="Premium">
              ✦
            </span>
          ) : null}
        </button>
      )
    })}
  </nav>
)
